import React, { useState, useRef, useEffect, useCallback } from 'react'
import { FontAwesomeIcon }                    from '@fortawesome/react-fontawesome'
import ReactMarkdown                          from 'react-markdown'
import remarkGfm                              from 'remark-gfm'
import consumer                               from 'channels/consumer'
import { Uploader }                           from '@shared/uploader'
import '../../../assets/stylesheets/components/_chat.scss'
import { dayLabel }                         from '@react/helpers/day_label'

// Friendly labels for known tools. Unknown tools keep the generic prefix so
// other chats using this component (MCP, diagnostic) are unaffected.
const toolLabel = status => {
  const labels = {
    search_code:    I18n.t('chats.tool_searching_code'),
    list_files:     I18n.t('chats.tool_searching_code'),
    read_file:      I18n.t('chats.tool_reading_file'),
    writing_answer: I18n.t('chats.tool_writing_answer')
  }
  return labels[status] || `Tool: ${status}`
}

const STALL_MS = 180000

const Chat = ({ channel, placeholder, disabled = false, onShow, onMessages, allowImages = true, onUseAnswer = null, emptyState = null, onBusyChange = null, maxSizeMb, onDone = null, hideInput = false, toolLabels = null, clearOnTool = false, interruptible = false, interruptPlaceholder = null, rows = false, userName = '', assistantName = '', enterSends = true, pendingNote = null }) => {
  const [text, setText]               = useState('')
  const [messages, setMessages]       = useState([])
  const [images, setImages]           = useState([])
  const [chatId, setChatId]           = useState(null)
  const [sending, setSending]         = useState(false)
  const [streaming, setStreaming]     = useState('')
  const [activeImage, setActiveImage] = useState(null)
  const [error, setError]             = useState(false)
  const [debug, setDebug]             = useState(null)
  const [status, setStatus]           = useState(null)
  const [uploading, setUploading]     = useState(false)
  const messagesRef                   = useRef(null)
  const subscriptionRef               = useRef(null)
  const uploaderRef                   = useRef(null)
  const textareaRef                   = useRef(null)

  const busy = disabled || uploading || (sending && !interruptible)

  // Broadcasts carry their run; once a newer run is known, an older one's are dropped.
  const runRef = useRef(null)
  const adoptRun = run => {
    if (!run || (runRef.current && run <= runRef.current)) return false

    const replaced = !!runRef.current
    runRef.current = run
    return replaced
  }

  const pending    = sending || !!streaming || !!status
  const pendingRef = useRef(pending)
  pendingRef.current = pending

  const onShowRef = useRef(onShow)
  onShowRef.current = onShow

  const onBusyChangeRef = useRef(onBusyChange)
  onBusyChangeRef.current = onBusyChange

  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  const labelFor = status => (toolLabels && toolLabels[status]) || toolLabel(status)

  useEffect(() => {
    if (onBusyChangeRef.current) onBusyChangeRef.current(pending)
  }, [pending])

  useEffect(() => () => {
    if (onBusyChangeRef.current) onBusyChangeRef.current(false)
  }, [])

  // The server owns the state of the run: an answer missed on the cable is
  // still readable here.
  const reconcile = useCallback(() => (
    onShowRef.current().then(data => {
      if (data.pending !== false) return false

      setMessages(data.messages || [])
      setStreaming('')
      setStatus(null)
      setSending(false)
      setError(!!data.error)
      setDebug(data.debug || null)
      if (onDoneRef.current) onDoneRef.current(data)
      return true
    }).catch(() => false)
  ), [])

  useEffect(() => {
    const el = uploaderRef.current
    if (!el) return

    const input = el.querySelector('input[type="file"]')
    if (!input) return

    const handler = () => { if (input.files.length > 0) setUploading(true) }
    input.addEventListener('change', handler)
    return () => input.removeEventListener('change', handler)
  }, [])

  const scrollToBottom = () => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }

  useEffect(scrollToBottom, [messages, streaming, images, error])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [text])

  // Scroll to bottom when container becomes visible (e.g. modal open)
  useEffect(() => {
    if (!messagesRef.current) return

    const observer = new ResizeObserver(scrollToBottom)
    observer.observe(messagesRef.current)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!chatId) return

    subscriptionRef.current = consumer.subscriptions.create(
      { channel, chat_id: chatId },
      {
        connected: () => {
          if (pendingRef.current) reconcile()
        },

        received: data => {
          if (data.run && runRef.current && data.run < runRef.current) return
          if (adoptRun(data.run)) setStreaming('')

          if (data.error) {
            setSending(false)
            setStreaming('')
            setStatus(null)
            setError(true)
            // The technical reason is never broadcast: only the server decides
            // who may read it back.
            reconcile()
            return
          }

          if (data.status) {
            if (data.status === 'writing_answer' || clearOnTool) setStreaming('')
            setStatus(data.status)
            return
          }

          if (data.done) {
            if (data.message) {
              setMessages(msgs => [...msgs, data.message])
              setStreaming('')
              setError(false)
            }
            setSending(false)
            setStatus(null)
            if (onDoneRef.current) onDoneRef.current(data)
          } else {
            setStatus(null)
            setStreaming(prev => prev + data.chunk)
          }
        }
      }
    )

    return () => {
      if (subscriptionRef.current) subscriptionRef.current.unsubscribe()
    }
  }, [chatId, reconcile])

  useEffect(() => {
    if (!sending) return

    const timer = setTimeout(() => {
      reconcile().then(recovered => {
        if (recovered) return

        setSending(false)
        setStatus(null)
        setStreaming('')
        setError(true)
      })
    }, STALL_MS)

    return () => clearTimeout(timer)
  }, [sending, streaming, status, reconcile])

  useEffect(() => {
    if (disabled) {
      setChatId(null)
      setMessages([])
      return
    }

    onShow().then(data => {
      adoptRun(data.run)
      setChatId(data.chat_id)
      setMessages(data.messages || [])
      setSending(!!data.pending)
      setError(!!data.error)
      setDebug(data.debug || null)
    })
  }, [disabled])

  const handleSend = () => {
    if (busy || (!text.trim())) return

    if (sending) {
      setStreaming('')
      setStatus(null)
    }

    const messageText   = text.trim()
    const currentImages = [...images]

    setText('')
    setImages([])
    setError(false)
    setDebug(null)
    setSending(true)

    const body = { text: messageText }

    if (currentImages.length > 0) {
      body.signed_ids = currentImages.map(img => img.signedId)
    }

    onMessages(body).then(data => {
      if (adoptRun(data.run)) setStreaming('')
      setChatId(data.chat_id)
      setMessages(data.messages || [])
    })
  }

  const handleRetry = () => {
    setError(false)
    setDebug(null)
    setSending(true)
    onMessages({ retry: true })
  }

  const onImagesUploaded = (signedIds, files) => {
    const newImages = signedIds.map((signedId, i) => ({ signedId, file: files[i] }))
    setImages(prev => [...prev, ...newImages])
    setUploading(false)
  }

  const removeImage = index => {
    setImages(images.filter((_, i) => i !== index))
  }

  return (
    <>
      <div className={`chat__body ${disabled ? 'chat--disabled' : ''}`}>
        <div className="chat__messages" ref={messagesRef}>
          { emptyState && messages.length === 0 && !sending && !streaming &&
            <div className="chat__empty">{emptyState}</div>
          }

          { rows && messages.map((msg, index) => {
            const at       = msg.at ? new Date(msg.at) : null
            const prev     = messages[index - 1]
            const prevAt   = prev?.at ? new Date(prev.at) : null
            const newDay   = at && (!prevAt || prevAt.toDateString() !== at.toDateString())
            const grouped  = !newDay && prev && prev.role === msg.role && prev.author_name === msg.author_name && at && prevAt && at - prevAt < GROUP_GAP_MS

            return (
              <React.Fragment key={index}>
                { newDay && <DayDivider date={at} /> }
                <Row role={msg.role} name={msg.role === 'user' ? (msg.author_name || userName) : assistantName}
                     time={at ? hourMinute(at) : msg.created_at} grouped={grouped}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                  { msg.images?.length > 0 &&
                    <div className="chat__images">
                      { msg.images.map((src, i) => <img key={i} src={src} onClick={() => setActiveImage(src)} />) }
                    </div>
                  }
                  { onUseAnswer && msg.role !== 'user' && msg.text &&
                    <div className="chat__row-actions">
                      <button type="button" className="chat__use-answer" onClick={() => onUseAnswer(msg.text)}>
                        <FontAwesomeIcon icon="reply" /> {I18n.t('chats.use_answer')}
                      </button>
                    </div>
                  }
                </Row>
              </React.Fragment>
            )
          })}

          { !rows && messages.map((msg, index) =>
            msg.role === 'user' ? (
              <div key={index} className="chat__bubble chat__bubble--user">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                { msg.images?.length > 0 &&
                  <div className="chat__images">
                    { msg.images.map((src, i) =>
                      <img key={i} src={src} onClick={() => setActiveImage(src)} />
                    )}
                  </div>
                }
                <div className="chat__date">{msg.created_at}</div>
              </div>
            ) : (
              <div key={index} className="chat__assistant">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                { onUseAnswer && msg.text &&
                  <button
                    type      = "button"
                    className = "chat__use-answer"
                    onClick   = {() => onUseAnswer(msg.text)}
                  >
                    <FontAwesomeIcon icon="reply" /> {I18n.t('chats.use_answer')}
                  </button>
                }
                <div className="chat__date">{msg.created_at}</div>
              </div>
            )
          )}

          { error &&
            <div className="chat__error">
              <div className="chat__bubble chat__bubble--error">
                {I18n.t('chats.error')}
              </div>
              {/* With no input the conversation is closed: the server refuses a retry. */}
              { !hideInput &&
                <span className="chat__retry text-pointer" onClick={handleRetry}>
                  <FontAwesomeIcon icon="redo" />
                </span>
              }
            </div>
          }

          { error && debug &&
            <details className="chat__debug">
              <summary>{I18n.t('chats.error_details')}</summary>
              <pre>{debug}</pre>
            </details>
          }

          {/* pendingNote: shown instead of what is being worked on. */}
          { pendingNote && (sending || status) && !streaming &&
            <div className="chat__pending-note">{pendingNote}</div>
          }

          { !pendingNote && sending && !streaming && !status &&
            <ThinkingIndicator text={I18n.t('chats.thinking')} />
          }

          { !pendingNote && status &&
            <ThinkingIndicator icon="wrench" text={labelFor(status)} />
          }

          { streaming && rows &&
            <Row role="assistant" name={assistantName}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{streaming}</ReactMarkdown><span className="chat__cursor" />
            </Row>
          }

          { streaming && !rows &&
            <div className="chat__assistant chat__assistant--streaming">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{streaming}</ReactMarkdown><span className="chat__cursor" />
            </div>
          }
        </div>

        { images.length > 0 &&
          <div className="chat__previews">
            { images.map((img, index) =>
              <ImagePreview key={index} file={img.file} onRemove={() => removeImage(index)} />
            )}
          </div>
        }

        { !hideInput &&
        <div className={`chat__input ${busy ? 'chat--disabled' : ''}`} onKeyDown={e => { if (enterSends && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}>
          { allowImages &&
            <div ref={uploaderRef}>
              <Uploader onUpload={onImagesUploaded} multiple images className="chat__image" maxSizeMb={maxSizeMb}>
                <FontAwesomeIcon icon="camera" />
              </Uploader>
            </div>
          }

          <textarea
            ref         = {textareaRef}
            value       = {text}
            onChange     = {e => setText(e.target.value)}
            placeholder = {sending && interruptPlaceholder ? interruptPlaceholder : placeholder}
            rows        = {1}
            disabled    = {busy}
          />

          <div className="chat__send d-flex-center a-s-stretch" onClick={handleSend}>
            <FontAwesomeIcon
              icon={sending && !interruptible && !pendingNote ? 'spinner' : 'paper-plane'}
              size="lg"
              spin={sending && !interruptible && !pendingNote}
              style={{ opacity: (busy || (!text.trim())) ? 0.4 : 1 }}
            />
          </div>
        </div>
        }
      </div>

      { activeImage &&
        <div className="chat__lightbox" onClick={() => setActiveImage(null)}>
          <img src={activeImage} />
        </div>
      }
    </>
  )
}

const initials = name => (name || '?').trim().split(/[\s\-_]+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase()

const GROUP_GAP_MS = 5 * 60 * 1000

const hourMinute = date => date.toLocaleTimeString(I18n.locale, { hour: '2-digit', minute: '2-digit' })

const DayDivider = ({ date }) => (
  <div className="ticket-ai-divider">
    <span className="ticket-ai-divider__label">{dayLabel(date)}</span>
  </div>
)

const Row = ({ role, name, time, grouped = false, children }) => (
  <div className={`chat__row chat__row--${role} ${grouped ? 'chat__row--grouped' : ''}`}>
    <span className="chat__row-avatar">
      { grouped
        ? <span className="chat__row-hover-time">{time}</span>
        : (role === 'user' ? initials(name) : <FontAwesomeIcon icon="robot" />) }
    </span>
    <div className="chat__row-main">
      { !grouped &&
        <div className="chat__row-head">
          <span className="chat__row-name">{name}</span>
          { role !== 'user' && <span className="chat__row-app">{I18n.t('ticket_assistant.app_badge')}</span> }
          { time && <span className="chat__row-time">{time}</span> }
        </div>
      }
      <div className="chat__row-body logs__message-body">{children}</div>
    </div>
  </div>
)

const ThinkingIndicator = ({ icon, text }) => (
  <div className="chat__assistant chat__assistant--status">
    { icon
      ? <FontAwesomeIcon icon={icon} className="chat__icon-pulse" />
      : <div className="chat__typing"><span /><span /><span /></div>
    }
    <span className="chat__thinking-text chat__shimmer">{text}</span>
  </div>
)

const ImagePreview = ({ file, onRemove }) => {
  const [url, setUrl] = useState(null)

  useEffect(() => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target.result
      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image/')) {
        setUrl(dataUrl)
      }
    }
    reader.readAsDataURL(file)
  }, [file])

  return (
    <div className="chat__preview">
      <img src={url} />
      <span className="chat__preview-remove" onClick={onRemove}>
        <FontAwesomeIcon icon="times" />
      </span>
    </div>
  )
}

export default Chat
